/**
 * Share moments — contextual text, a stable deep link and, where supported,
 * a locally rendered Living Programme card. Personal supporter choices are
 * never encoded into the URL or sent to COYG to render.
 */
import { createShareCardFile, type ShareCard } from './share-card';
import { recordGrowth } from './growth';

export const CANONICAL = 'https://coyg.org';

const pathLink = (surface: string, value: string): string => `${CANONICAL}/${surface}/${encodeURIComponent(value)}`;

export function matchLink(matchId: string): string { return pathLink('match', matchId); }
export function playerLink(playerId: string): string { return pathLink('player', playerId); }
export function entryLink(entryId: string): string { return pathLink('story', entryId); }
export function loreLink(matchId: string): string { return pathLink('match', matchId); }
export function editionLink(date: string): string { return pathLink('edition', date); }
export function yearLink(year: string): string { return pathLink('year', year); }
export function northBankLink(room = 'all', matchId?: string | null): string {
  const url = new URL(pathLink('north-bank', room.toLowerCase()));
  if (room.toLowerCase() === 'match' && matchId) url.searchParams.set('thread', matchId);
  return url.toString();
}

/** Sharing the app itself — the canonical URL with no deep-link params. */
export const APP_SHARE: ShareMoment = {
  title: 'COYG',
  text: 'COYG — the Arsenal matchday companion.',
  url: CANONICAL,
  card: {
    eyebrow: 'THE LIVING PROGRAMME',
    headline: 'What matters to Gunners, right now.',
    detail: 'Matches · North Bank · Club memory',
    stamp: 'COYG.ORG',
  },
};

export interface ShareMoment {
  title: string;
  text: string;
  url: string;
  /** Rendered locally. Never used by the Worker metadata resolver. */
  card?: ShareCard;
}

export type ShareResult = 'shared' | 'copied' | 'cancelled' | 'failed';

function aborted(error: unknown): boolean {
  return typeof DOMException !== 'undefined' && error instanceof DOMException && error.name === 'AbortError';
}

export async function share(moment: ShareMoment): Promise<ShareResult> {
  recordGrowth('share-opened');
  const payload = `${moment.text}\n${moment.url}`;
  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    let cardFile: File | null = null;
    let fileShareSupported = false;
    if (moment.card && typeof navigator.canShare === 'function') {
      cardFile = await createShareCardFile(moment.card);
      try { fileShareSupported = Boolean(cardFile && navigator.canShare({ files: [cardFile] })); } catch { /* text share below */ }
    }
    if (cardFile && fileShareSupported) {
      try {
        await navigator.share({ title: moment.title, text: moment.text, url: moment.url, files: [cardFile] });
        recordGrowth('share-completed'); return 'shared';
      } catch (error) {
        if (aborted(error)) return 'cancelled';
      }
    }
    try {
      await navigator.share({ title: moment.title, text: moment.text, url: moment.url });
      recordGrowth('share-completed'); return 'shared';
    } catch (error) {
      if (aborted(error)) return 'cancelled';
    }
  }
  try {
    await navigator.clipboard.writeText(payload);
    recordGrowth('share-completed');
    return 'copied';
  } catch {
    return 'failed';
  }
}
